import { User, UserListItem, UserRole } from '@/types/user';
import { PermissionLevel, PermissionAction } from '@/types/permissions';
import { normalizeUserRole } from '@/utils/userRoles';
import { canActOnUser, getAccessRole } from '@/utils/rbac';

export type { PermissionLevel, PermissionAction } from '@/types/permissions';

const ROOT_PERMISSIONS: PermissionAction[] = [
  'view_dashboard',
  'view_curation',
  'view_timeline',
  'view_settings',
  'edit_account',
  'view_users',
  'create_users',
  'edit_users',
  'delete_users',
  'view_partners',
  'create_partners',
  'edit_partners',
  'delete_partners',
  'view_reports',
  'create_reports',
  'edit_reports',
  'send_reports',
  'delete_reports',
  'view_sources',
  'edit_sources',
  'view_content',
  'create_content',
  'edit_content',
  'view_contexts',
  'view_brands_sectors',
  'view_creators',
];

const ADMIN_PERMISSIONS: PermissionAction[] = [
  'view_settings',
  'edit_account',
  'view_users',
  'create_users',
  'edit_users',
  'delete_users',
  'view_partners',
  'create_partners',
  'edit_partners',
  'delete_partners',
  'view_reports',
  'create_reports',
  'edit_reports',
  'send_reports',
  'delete_reports',
  'view_sources',
  'view_content',
  'create_content',
  'edit_content',
  'view_contexts',
  'view_brands_sectors',
  'view_creators',
];

const CURATOR_PERMISSIONS: PermissionAction[] = [
  'view_settings',
  'edit_account',
  'view_reports',
  'create_reports',
  'edit_reports',
  'send_reports',
  'view_partners',
  'edit_partners',
  'view_users',
  'create_users',
  'edit_users',
];

const SUPPORT_PERMISSIONS: PermissionAction[] = [
  'view_settings',
  'edit_account',
  'view_reports',
  'send_reports',
  'view_users',
  'create_users',
  'edit_users',
  'view_partners',
];

const GUEST_PERMISSIONS: PermissionAction[] = [
  'view_settings',
  'edit_account',
  'view_reports',
];

export function getUserPermissionLevel(user: User | null): PermissionLevel {
  if (!user) return 'CURATOR';

  const accessRole = getAccessRole(user);
  if (accessRole === 'ADMIN') return 'ROOT';
  if (accessRole === 'MODERATOR') return 'ADMIN';
  if (accessRole === 'SUPPORT') return 'SUPPORT';
  if (accessRole === 'GUEST') return 'GUEST';

  switch (normalizeUserRole(user.role)) {
    case 'ADMIN_ROOT':
      return 'ROOT';
    case 'ADMIN':
      return 'ADMIN';
    default:
      return 'CURATOR';
  }
}

export function getUserPermissions(user: User | null): PermissionAction[] {
  const level = getUserPermissionLevel(user);

  switch (level) {
    case 'ROOT':
      return ROOT_PERMISSIONS;
    case 'ADMIN':
      return ADMIN_PERMISSIONS;
    case 'SUPPORT':
      return SUPPORT_PERMISSIONS;
    case 'GUEST':
      return GUEST_PERMISSIONS;
    default:
      return CURATOR_PERMISSIONS;
  }
}

export function hasPermission(user: User | null, action: PermissionAction): boolean {
  return getUserPermissions(user).includes(action);
}

export function canOperateReports(user: User | null): boolean {
  return (
    hasPermission(user, 'create_reports') ||
    hasPermission(user, 'edit_reports') ||
    hasPermission(user, 'send_reports') ||
    hasPermission(user, 'delete_reports')
  );
}

export function getAssignableUserRoles(user: User | null): UserRole[] {
  if (!user) return [];

  const accessRole = getAccessRole(user);
  if (accessRole === 'ADMIN') {
    return ['ADMIN_ROOT', 'ADMIN', 'CURATOR'];
  }

  if (accessRole === 'MODERATOR') {
    return ['CURATOR'];
  }

  return [];
}

export function canDeleteUser(
  actor: User | null,
  target: User | UserListItem | null
): boolean {
  if (!actor || !target) return false;
  if (!hasPermission(actor, 'delete_users')) return false;
  if (actor.email === target.email) return false;
  return canActOnUser(actor, target);
}

export function canEditUser(
  actor: User | null,
  target: User | UserListItem | null
): boolean {
  if (!actor || !target) return false;
  if (!hasPermission(actor, 'edit_users')) return false;
  return canActOnUser(actor, target);
}

export function canAccessRoute(user: User | null, route: string): boolean {
  if (!user) return false;

  if (route === '/app' || route === '/app/') {
    return false;
  }

  if (route === '/app/settings/account') {
    return hasPermission(user, 'edit_account');
  }

  if (route === '/app/settings/users') {
    return hasPermission(user, 'view_users');
  }

  if (route === '/app/settings/partners') {
    return hasPermission(user, 'view_partners');
  }

  if (route === '/app/settings/content/contexts') {
    return hasPermission(user, 'view_contexts');
  }

  if (route === '/app/settings/content/brands-sectors') {
    return hasPermission(user, 'view_brands_sectors');
  }

  if (route === '/app/settings/content/creators') {
    return hasPermission(user, 'view_creators');
  }

  if (route.startsWith('/app/timeline')) {
    return false;
  }

  if (route.startsWith('/app/curation')) {
    return false;
  }

  if (route.startsWith('/app/reports/new')) {
    return hasPermission(user, 'create_reports');
  }

  if (/^\/app\/reports\/[^/]+\/edit/.test(route)) {
    return hasPermission(user, 'edit_reports');
  }

  if (route.startsWith('/app/reports')) {
    return hasPermission(user, 'view_reports');
  }

  if (route.startsWith('/app/settings/sources')) {
    return hasPermission(user, 'view_sources');
  }

  if (route.startsWith('/app/settings/content')) {
    return hasPermission(user, 'view_content');
  }

  if (route.startsWith('/app/settings')) {
    return hasPermission(user, 'view_settings');
  }

  return true;
}
